import React, { useState } from "react";
import { Button, ButtonVariants, Icons, IconsVariants, UserName } from "../../../UI";
import moment from "moment";
import { useParams } from "react-router-dom";
import { Links, LinksVariants } from "../../../UI/Links/Links";
import { useFollowUserMutation, useUnfollowUserMutation } from '../../../apis';
import { DialogComponent } from "../../../components/DialogComponent/DialogComponent";

interface UserDataProps {
  displayName?: string;
  userName?: string;
  createdAt?: string;
  isFollowing?: boolean;
  followers?: number;
  following?: number;
}

export const UserData: React.FC<UserDataProps> = ({
  displayName, userName, createdAt, isFollowing, followers, following }) => {
  // TODO add user createdAt, folowers in localStorage

  const [followUser, { isLoading: isLoadingAfterFollow }] = useFollowUserMutation();
  const [unfollowUser, { isLoading: isLoadingAfterUnollow }] = useUnfollowUserMutation();

  const { userId } = useParams<{ userId: string }>();
  const currentUserId = localStorage.getItem("userId");
  const isCurrentUser = userId === currentUserId;
  const [userIsFollowed, setUserIsFollowed] = useState(isFollowing);
  const [errorDialog, setErrorDialog] = useState({
    isOpen: false,
    message: null as string | null,
  });

  const handleFollowUser = async () => {
    const result = await followUser({ userId: userId! });

    if ('error' in result && result.error) {
      const message = 'message' in result.error ? result.error.message : 'Unknown error';
      setErrorDialog({ isOpen: true, message: message as string });
    } else {
      setUserIsFollowed(curr => !curr);
    }
  };

  const handleUnfollowUser = async () => {
    const result = await unfollowUser({ userId: userId! });

    if ('error' in result && result.error) {
      const message = 'message' in result.error ? result.error.message : 'Unknown error';
      setErrorDialog({ isOpen: true, message: message as string });
    } else {
      setUserIsFollowed(curr => !curr);
    }
  };

  return (
    <>
      <DialogComponent
        isOpen={errorDialog.isOpen}
        closeDialog={() => setErrorDialog({ isOpen: false, message: null })}
        content={errorDialog.message ?? ''}
      />
      <div className="flex flex-col mt-app-2xl pl-app-sm relative">
        <div className="flex justify-end absolute right-0 top-[-15px]">
          {/* Checking if there is logged in user, and that this is not his profile page */}
          {currentUserId && !isCurrentUser
            ? (
              <>
                {userIsFollowed
                  ? (
                    <Button
                      variant={ButtonVariants.OUTLINE_ON_HOVER_ROUNDED_DANGER_LARGE}
                      isLoading={isLoadingAfterUnollow}
                      onClick={handleUnfollowUser}
                    >
                      Unfollow
                    </Button>
                  )
                  : (
                    <Button
                      variant={ButtonVariants.ROUNDED_GRAY_LARGE}
                      isLoading={isLoadingAfterFollow}
                      onClick={handleFollowUser}
                    >
                      Follow
                    </Button>
                  )}
              </>
            )
            : null}
        </div>
        <h2 className="text-app-lg font-semibold">{displayName || "User"}</h2>
        <span className="text-app-gray text-app-mid mt-1 ">
          {userName ? <UserName userName={userName} /> : "User"}
        </span>
        <div className="flex gap-2 mt-app-sm">
          <Icons
              variant={IconsVariants.CALENDAR}
              classes="w-app-xl fill-app-gray"
          />

          <span className="text-app-mid mt-0.5 text-app-gray">
            Joined at: {moment(createdAt).format('MMMM YYYY')}
          </span>
        </div>
        <div className="flex mt-app-sm">
          <div className="mr-app-s">
            <Links to={`/profile/${userId}/followers`} variant={LinksVariants.PRIMARY} >
              <span className="text-app-mid font-bold mr-[2px]">{followers}</span>
              <span className="text-app-gray">Followers</span>
            </Links>
          </div>
          <div >
            <Links to={`/profile/${userId}/following`} variant={LinksVariants.PRIMARY}>
              <span className="text-app-mid font-bold mr-[2px]">{following}</span>
              <span className="text-app-gray">Following</span>
            </Links>
          </div>
        </div>
      </div>
    </>
  );
};
